import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CategoryService } from 'src/app/service/category.service';
import { OfferService } from 'src/app/service/offer.service';
import { ToastrNotificationService } from 'src/app/service/toastr-notification.service';
import { environment } from 'src/environments/environment';

export interface CategoryRequirement {
  categoryId: string;
  qty: number;
}

export interface CombinedCategoryRequirement {
  categoryIds: string[];
  groupName: string;
  qty: number;
}

@Component({
  selector: 'app-add-edit-offer',
  templateUrl: './add-edit-offer.component.html',
  styleUrls: ['./add-edit-offer.component.scss'],
})
export class AddEditOfferComponent implements OnInit {
  isAdd: boolean = true;
  offerId: string = '';
  spinner: boolean = false;
  isSubmitting: boolean = false;
  environmentURL = environment.apiUrl;

  allCategories: any[] = [];
  categoriesLoading: boolean = false;

  // Image Upload
  currentFile: File | null = null;
  imagePreview: string = '';
  existingImageUrl: string = '';

  // Main Form Group
  offerForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(2)]),
    description: new FormControl(''),
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    isActive: new FormControl<boolean>(true),
    isLuckyDraw: new FormControl<boolean>(false),
    luckyDrawQty: new FormControl<number | null>(null),
    termsAndConditions: new FormControl(''),
    sortOrder: new FormControl<number>(0),
  });

  // Category Requirements
  categoryRows: CategoryRequirement[] = [];
  combinedCategoryRows: CombinedCategoryRequirement[] = [];

  // Rules dynamic list
  rulesList: string[] = [''];

  constructor(
    private offerService: OfferService,
    private categoryService: CategoryService,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private toastr: ToastrNotificationService
  ) { }

  ngOnInit(): void {
    this.offerId = this.route.snapshot.params['id'];
    this.loadAllCategories();

    if (this.offerId) {
      this.isAdd = false;
      this.titleService.setTitle('Edit Offer');
      this.loadOfferDetails(this.offerId);
    } else {
      this.isAdd = true;
      this.titleService.setTitle('Add Offer');
      // Add initial default row for regular milestone
      this.addCategoryRow();
    }
  }

  loadAllCategories(): void {
    this.categoriesLoading = true;
    this.categoryService.getAllCategories().subscribe({
      next: (response) => {
        this.categoriesLoading = false;
        if (response && response.success) {
          this.allCategories = response.categories || [];
        } else if (Array.isArray(response)) {
          this.allCategories = response;
        }
      },
      error: (error) => {
        this.categoriesLoading = false;
        this.toastr.error('Failed to load categories', 'Error');
      },
    });
  }

  loadOfferDetails(id: string): void {
    this.spinner = true;
    this.offerService.getOfferById(id).subscribe({
      next: (response) => {
        this.spinner = false;
        if (response && response.success && response.offer) {
          const offer = response.offer;

          this.offerForm.patchValue({
            name: offer.name,
            description: offer.description || '',
            startDate: offer.startDate ? new Date(offer.startDate) : null,
            endDate: offer.endDate ? new Date(offer.endDate) : null,
            isActive: offer.isActive !== undefined ? offer.isActive : true,
            isLuckyDraw: offer.isLuckyDraw || false,
            luckyDrawQty: offer.isLuckyDraw ? offer.totalQty : null,
            termsAndConditions: offer.termsAndConditions || '',
            sortOrder: offer.sortOrder ?? 0,
          });

          if (offer.imageUrl) {
            this.existingImageUrl = offer.imageUrl;
            this.imagePreview = `${this.environmentURL}/${offer.imageUrl}`;
          }

          // Populate rules
          if (offer.rules && offer.rules.length > 0) {
            this.rulesList = [...offer.rules];
          } else {
            this.rulesList = [''];
          }

          // Populate categories
          this.categoryRows = [];
          this.combinedCategoryRows = [];

          if (offer.categories && Array.isArray(offer.categories)) {
            offer.categories.forEach((cat: any) => {
              if (cat.isCombined) {
                const ids = (cat.categoryIds || []).map((c: any) =>
                  typeof c === 'object' ? c._id : c
                );
                this.combinedCategoryRows.push({
                  categoryIds: ids,
                  groupName: cat.groupName || '',
                  qty: cat.qty || 0,
                });
              } else {
                const firstId =
                  cat.categoryIds && cat.categoryIds.length > 0
                    ? typeof cat.categoryIds[0] === 'object'
                      ? cat.categoryIds[0]._id
                      : cat.categoryIds[0]
                    : '';
                this.categoryRows.push({
                  categoryId: firstId,
                  qty: cat.qty || 0,
                });
              }
            });
          }

          if (!offer.isLuckyDraw && this.categoryRows.length === 0 && this.combinedCategoryRows.length === 0) {
            this.addCategoryRow();
          }
        }
      },
      error: (error) => {
        this.spinner = false;
        this.toastr.error(
          error.error?.error || error.message || 'Could not load offer details',
          'Error'
        );
      },
    });
  }

  // Individual Category Targets
  addCategoryRow(): void {
    this.categoryRows.push({
      categoryId: '',
      qty: 0,
    });
  }

  removeCategoryRow(index: number): void {
    if (this.categoryRows.length > 1 || this.combinedCategoryRows.length > 0) {
      this.categoryRows.splice(index, 1);
    } else {
      this.categoryRows[0] = { categoryId: '', qty: 0 };
    }
  }

  // Combined Category Targets (Custom Option)
  addCombinedCategoryRow(): void {
    this.combinedCategoryRows.push({
      categoryIds: [],
      groupName: '',
      qty: 0,
    });
  }

  removeCombinedCategoryRow(index: number): void {
    this.combinedCategoryRows.splice(index, 1);
  }

  getCategoryName(id: string): string {
    const found = this.allCategories.find((c) => c._id === id);
    return found ? found.name : '';
  }

  getCombinedCategoryNames(ids: string[]): string[] {
    if (!ids || !Array.isArray(ids)) return [];
    return ids.map((id) => this.getCategoryName(id)).filter((name) => name);
  }

  // Total Quantity Calculation
  get calculatedMilestoneTotalQty(): number {
    let total = 0;
    this.categoryRows.forEach((row) => {
      total += Number(row.qty) || 0;
    });
    this.combinedCategoryRows.forEach((group) => {
      total += Number(group.qty) || 0;
    });
    return total;
  }

  get effectiveTotalQty(): number {
    if (this.offerForm.value.isLuckyDraw) {
      return Number(this.offerForm.value.luckyDrawQty) || 0;
    }
    return this.calculatedMilestoneTotalQty;
  }

  // Rules Dynamic Management
  addRule(): void {
    this.rulesList.push('');
  }

  removeRule(index: number): void {
    if (this.rulesList.length > 1) {
      this.rulesList.splice(index, 1);
    } else {
      this.rulesList[0] = '';
    }
  }

  updateRule(index: number, event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.rulesList[index] = val;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // Image Upload Handler
  onFileSelected(event: any): void {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const file: File = selectedFiles[0];

      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        this.toastr.error('Only JPG, PNG and WEBP images are allowed', 'Invalid File');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.toastr.error('Image size must be less than 10MB', 'File Too Large');
        return;
      }

      this.currentFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(): void {
    this.currentFile = null;
    this.imagePreview = '';
    this.existingImageUrl = '';
  }

  // Submit Logic
  onSubmit(): void {
    if (this.offerForm.invalid) {
      this.toastr.error('Please fill in all required fields properly', 'Validation Error');
      return;
    }

    const formVal = this.offerForm.value;
    const isLuckyDraw = !!formVal.isLuckyDraw;

    if (formVal.startDate && formVal.endDate) {
      const start = new Date(formVal.startDate);
      const end = new Date(formVal.endDate);
      if (end < start) {
        this.toastr.error('End Date cannot be earlier than Start Date', 'Date Error');
        return;
      }
    }

    if (isLuckyDraw) {
      if (!formVal.luckyDrawQty || Number(formVal.luckyDrawQty) <= 0) {
        this.toastr.error('Please specify eligible total scan quantity for Lucky Draw', 'Validation Error');
        return;
      }
    } else {
      // Validate milestone categories
      const hasValidSingle = this.categoryRows.some((r) => r.categoryId && Number(r.qty) > 0);
      const hasValidCombined = this.combinedCategoryRows.some(
        (g) => g.categoryIds && g.categoryIds.length > 0 && Number(g.qty) > 0
      );

      if (!hasValidSingle && !hasValidCombined) {
        this.toastr.error(
          'Please configure at least one category requirement with valid quantity',
          'Validation Error'
        );
        return;
      }
    }

    this.isSubmitting = true;

    // Prepare payload
    const formData = new FormData();
    formData.append('name', formVal.name || '');
    formData.append('description', formVal.description || '');
    if (formVal.startDate) {
      formData.append('startDate', new Date(formVal.startDate).toISOString());
    }
    if (formVal.endDate) {
      formData.append('endDate', new Date(formVal.endDate).toISOString());
    }
    formData.append('isActive', String(formVal.isActive));
    formData.append('isLuckyDraw', String(isLuckyDraw));
    formData.append('totalQty', String(this.effectiveTotalQty));
    formData.append('termsAndConditions', formVal.termsAndConditions || '');
    formData.append('sortOrder', String(Number(formVal.sortOrder) || 0));

    // Cleaned rules array
    const cleanedRules = this.rulesList
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
    formData.append('rules', JSON.stringify(cleanedRules));

    // Categories array
    if (!isLuckyDraw) {
      const categoriesPayload: any[] = [];

      // Single category targets
      this.categoryRows.forEach((row) => {
        if (row.categoryId && Number(row.qty) > 0) {
          categoriesPayload.push({
            categoryIds: [row.categoryId],
            categoryNames: [this.getCategoryName(row.categoryId)],
            qty: Number(row.qty),
            isCombined: false,
            groupName: '',
          });
        }
      });

      // Combined category targets
      this.combinedCategoryRows.forEach((group) => {
        if (group.categoryIds && group.categoryIds.length > 0 && Number(group.qty) > 0) {
          const names = this.getCombinedCategoryNames(group.categoryIds);
          const autoGroupName = names.join(' + ');
          categoriesPayload.push({
            categoryIds: group.categoryIds,
            categoryNames: names,
            qty: Number(group.qty),
            isCombined: true,
            groupName: group.groupName?.trim() ? group.groupName.trim() : autoGroupName,
          });
        }
      });

      formData.append('categories', JSON.stringify(categoriesPayload));
    } else {
      formData.append('categories', JSON.stringify([]));
    }

    // Attach Image if new file selected
    if (this.currentFile) {
      formData.append('image', this.currentFile);
    }

    if (this.isAdd) {
      this.offerService.createOffer(formData).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this.toastr.success('Offer created successfully!', 'Success');
          this.router.navigate(['/offer']);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.toastr.error(
            err.error?.error || err.message || 'Failed to create offer',
            'Error'
          );
        },
      });
    } else {
      formData.append('offerId', this.offerId);
      this.offerService.updateOffer(formData).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this.toastr.success('Offer updated successfully!', 'Success');
          this.router.navigate(['/offer']);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.toastr.error(
            err.error?.error || err.message || 'Failed to update offer',
            'Error'
          );
        },
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/offer']);
  }
}
